import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  SwapOutlined,
  UnorderedListOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import { dataGridTextSorter } from '../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import DataGrid from '../../../../common/DataGrid/DataGrid.jsx';
import IconButton from '../../../../common/controls/IconButton/IconButton.jsx';
import { useGetStudyProgramsForIngProjectsQuery } from '../../../../../redux/services/study_programs.jsx';

const IngProjectsActiveStudentsTable = ({
  students = [],
  viewMode = 'current',
  onEditClick,
  onDeleteClick,
  onViewApplicationsClick,
}) => {
  const { t } = useTranslation();
  const isAcceptedView = viewMode === 'accepted';
  const { data: studyPrograms } = useGetStudyProgramsForIngProjectsQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const studyProgramOptions = useMemo(
    () =>
      [...new Set(
        (Array.isArray(studyPrograms) ? studyPrograms : [])
          .filter(
            (program) =>
              Number(program?.study_program_type_id ?? 0) === 2 &&
              program?.active !== 0 &&
              program?.shortcut,
          )
          .map((program) => program.shortcut)
          .filter(Boolean),
      )].sort((a, b) => String(a).localeCompare(String(b))),
    [studyPrograms],
  );

  const studyProgramFilterProps = useMemo(
    () => ({
      filters: studyProgramOptions.map((value) => ({
        text: value,
        value,
      })),
      onFilter: (value, record) => String(record?.study_program ?? '') === String(value),
      width: 150,
    }),
    [studyProgramOptions],
  );

  const lastNameFilterProps = useDataGridTextFilter('last_name');
  const firstNameFilterProps = useDataGridTextFilter('first_name');
  const studyGroupFilterProps = useDataGridTextFilter('study_group');
  const projectNameFilterProps = useDataGridTextFilter('project_name');
  const assignedProjectNameFilterProps = useDataGridTextFilter(
    'assigned_project_name',
  );
  const yearOfStudyFilterProps = useDataGridTextFilter('year_of_study');
  const averageGradeFilterProps = useDataGridTextFilter('average_grade');
  const acceptedYearFilterProps = useDataGridTextFilter('accepted_year');

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: t('Priezvisko'),
        dataIndex: 'last_name',
        ellipsis: true,
        width: '15%',
        sorter: dataGridTextSorter('last_name'),
        defaultSortOrder: 'ascend',
        render: (value, row) => value || row?.full_name || '–',
        ...lastNameFilterProps,
      },
      {
        title: t('Meno'),
        dataIndex: 'first_name',
        ellipsis: true,
        width: '15%',
        sorter: dataGridTextSorter('first_name'),
        render: (value, row) => value || row?.full_name || '–',
        ...firstNameFilterProps,
      },
      {
        title: t('Št. skupina'),
        dataIndex: 'study_group',
        width: '10%',
        ellipsis: true,
        sorter: dataGridTextSorter('study_group'),
        render: (value) => value || '–',
        ...studyGroupFilterProps,
      },
      {
        title: t('Št. program'),
        dataIndex: 'study_program',
        width: '10%',
        ellipsis: true,
        sorter: dataGridTextSorter('study_program'),
        render: (value) => value || '–',
        ...studyProgramFilterProps,
      },
    ];

    if (isAcceptedView) {
      return [
        ...baseColumns,
        {
          title: t('Priemer'),
          dataIndex: 'average_grade',
          width: '10%',
          sorter: (a, b) =>
            Number(a.average_grade ?? 0) - Number(b.average_grade ?? 0),
          render: (value) => value || '–',
          ...averageGradeFilterProps,
        },
        {
          title: t('Priradený na projekt'),
          dataIndex: 'assigned_project_name',
          width: '30%',
          ellipsis: true,
          sorter: dataGridTextSorter('assigned_project_name'),
          render: (value) => value || '–',
          ...assignedProjectNameFilterProps,
        },

        {
          title: t('Rok prijatia'),
          dataIndex: 'accepted_year',
          width: '10%',
          sorter: (a, b) =>
            Number(a.accepted_year ?? 0) - Number(b.accepted_year ?? 0),
          render: (value) => value || '–',
          ...acceptedYearFilterProps,
        },
      ];
    }

    return [
      ...baseColumns,
      {
        title: t('Projekt'),
        dataIndex: 'project_name',
        width: '45%',
        ellipsis: true,
        sorter: dataGridTextSorter('project_name'),
        render: (value) => value || '–',
        ...projectNameFilterProps,
      },
      {
        title: t('Rok nástupu'),
        dataIndex: 'year_of_study',
        width: '10%',
        sorter: (a, b) => a.year_of_study - b.year_of_study,
        render: (value) => value || '–',
        ...yearOfStudyFilterProps,
      },
    ];
  }, [
    t,
    isAcceptedView,
    lastNameFilterProps,
    firstNameFilterProps,
    studyGroupFilterProps,
    studyProgramFilterProps,
    projectNameFilterProps,
    assignedProjectNameFilterProps,
    yearOfStudyFilterProps,
    averageGradeFilterProps,
    acceptedYearFilterProps,
  ]);

  return (
    <DataGrid
      columns={columns}
      dataSource={students}
      rowKey="id"
      onRowClick={false}
      pagination={{
        pageSize: 20,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
      }}
      customSettings={(row) => (
        <div style={{ display: 'flex' }}>
          <IconButton
            icon={<UnorderedListOutlined />}
            tooltip={t('Zobraziť prihlášku študenta')}
            onClick={(event) => {
              event.stopPropagation();
              onViewApplicationsClick?.(row);
            }}
          />
        </div>
      )}
      onEditClick={onEditClick}
      editIcon={<SwapOutlined />}
      editTooltip={t('Zmeniť projekt')}
      onDelete={onDeleteClick}
      deleteIcon={<UserDeleteOutlined />}
      deleteTooltip={
        isAcceptedView
          ? t('Odstrániť študenta zo zoznamu prijatých')
          : t('Odobrať priradenie')
      }
      deleteModalTitle={
        isAcceptedView
          ? t('Odstrániť prijatého študenta')
          : t('Odobrať priradenie študenta')
      }
      seriousDeleteQuestion={(row) =>
        isAcceptedView
          ? t('Naozaj chcete odstrániť študenta {{name}} zo zoznamu prijatých?', {
              name: row?.full_name || '',
            })
          : t('Naozaj chcete odobrať študenta {{name}} z projektu {{project}}?', {
              name: row?.full_name || '',
              project: row?.project_name || '',
            })
      }
      scroll={{ x: 'max-content' }}
    />
  );
};

IngProjectsActiveStudentsTable.propTypes = {
  students: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      student_id: PropTypes.number,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      full_name: PropTypes.string,
      study_group: PropTypes.string,
      study_program: PropTypes.string,
      project_name: PropTypes.string,
      assigned_project_name: PropTypes.string,
      year_of_study: PropTypes.number,
      average_grade: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      accepted_year: PropTypes.number,
    }),
  ),
  viewMode: PropTypes.oneOf(['current', 'accepted', 'archive']),
  onEditClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  onViewApplicationsClick: PropTypes.func,
};

export default IngProjectsActiveStudentsTable;
